/**
 * Pure helpers backing the "My Tasks" page (`/projects/{pid}/me`).
 *
 * The page surfaces both WBS rows (`WbsItemDoc`) and migration tasks
 * (`TaskDoc`) under a single normalized shape (`MyTaskItem`) so the row
 * renderer + tab filters don't have to branch on the source on every
 * render. The conversion + filter logic lives here so it's
 * exhaustively unit-testable without React or Firestore.
 *
 * Tab semantics:
 *
 *   - 今日: undone, has a concrete `endDate`, due today or earlier.
 *   - 今週: undone, EITHER due by end-of-week OR has no `endDate`
 *           (migration tasks have no native due date — they live on
 *           the wider "this week" surface so they don't get lost).
 *   - 待たせ中: undone, AND at least one item in the project's
 *           `dependsOn` graph names this item as a predecessor.
 *           Operates on the union of WBS-item dependsOn + migration-task
 *           dependsOn so the cross-source view is honoured.
 *   - 完了済 (直近 14 日): status === 'done', AND the most recent of
 *           `actualEnd` / `endDate` / `updatedAt` is within 14 days of
 *           `now`.
 *
 * The "undone" predicate is:
 *
 *   status !== 'done' && status !== 'cancelled'
 *
 * Migration tasks have no native status — `taskToMyTask` synthesises
 * `'in_progress'` for them so the undone predicate naturally lets them
 * through. Migration tasks therefore never appear in the 完了 tab — that
 * surface is WBS-only by construction. (Per-run completion lives in
 * `ExecutionDoc` and is out of scope for this view.)
 */
import {
  readWbsItemSystems,
  type AssigneeUserMappingDoc,
  type TaskDoc,
  type Timestamp,
  type UserId,
  type WbsItemDoc,
  type WbsItemStatus,
} from '@mct/shared';

/** Discriminator + carrier for the source doc, in case a renderer needs the original. */
export type MyTaskSource = 'wbs' | 'task';

/**
 * Normalised row shape consumed by the My Tasks page. Both WBS rows and
 * migration tasks fold into this shape so the table / row component
 * doesn't branch on the source.
 *
 * `progress` for migration tasks is always 0 — the "+10%" affordance is
 * gated on `source === 'wbs'`.
 */
export interface MyTaskItem {
  id: string;
  source: MyTaskSource;
  name: string;
  /** Synthesised for migration tasks (always `'in_progress'`). */
  status: WbsItemStatus;
  /** 0..100 — only meaningful for WBS rows; migration tasks pin to 0. */
  progress: number;
  /** Native due date for WBS rows; `null` for migration tasks. */
  endDate: Date | null;
  /** "領域" for WBS, "category" for migration tasks. */
  area: string;
  vendor: string;
  assigneeId: string | null;
  dependsOn: readonly string[];
  /**
   * Recency timestamp used by 完了 tab's 14-day window. Falls back from
   * `actualEnd → endDate → updatedAt` so we never miss a doc that was
   * edited recently but lacks the cleaner field.
   */
  recencyTime: number;
  /** Original doc — handed back so the detail panel can render it. */
  raw: WbsItemDoc | TaskDoc;
}

function tsToDate(t: Timestamp | null): Date | null {
  if (!t) return null;
  // Firestore JS SDK Timestamp; the test helpers also expose toMillis.
  return new Date(t.toMillis());
}

/** Wrap a WBS row in the unified shape. Returns the same fields the page uses. */
export function wbsItemToMyTask(item: WbsItemDoc): MyTaskItem {
  const end = tsToDate(item.plannedEnd);
  const actualEnd = tsToDate(item.actualEnd);
  const updated = tsToDate(item.updatedAt);
  const recency = (actualEnd ?? end ?? updated)?.getTime() ?? 0;
  return {
    id: item.itemId,
    source: 'wbs',
    name: item.name,
    status: item.status,
    progress: clampProgress(item.progress),
    endDate: end,
    // Primary 領域 (this view keeps a single area string).
    area: readWbsItemSystems(item)[0] ?? '',
    vendor: item.vendor,
    assigneeId: item.assigneeId,
    dependsOn: item.dependsOn,
    recencyTime: recency,
    raw: item,
  };
}

/**
 * Wrap a migration task in the unified shape. Migration tasks have no
 * native progress / status / due-date — we synthesise sensible defaults
 * so the same filter / row code path can render them.
 */
export function taskToMyTask(task: TaskDoc): MyTaskItem {
  const updated = tsToDate(task.updatedAt);
  return {
    id: task.taskId,
    source: 'task',
    name: task.name,
    status: 'in_progress',
    progress: 0,
    endDate: null,
    area: task.category,
    vendor: '',
    assigneeId: task.assigneeId,
    dependsOn: task.dependsOn,
    recencyTime: updated?.getTime() ?? 0,
    raw: task,
  };
}

function clampProgress(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

/**
 * Patch shape returned by the progress-bar click handler. Kept loose
 * (Record + optional fields) so callers can spread it into
 * `updateWbsItem`'s `UpdateWbsItemPatch` without a circular import.
 *
 * Behavioural rules:
 *   - clamp to [0, 100]
 *   - `progressMode: 'manual'` always — clicking the bar opts the row
 *     out of children rollup, matching the detail panel's slider.
 *   - status auto-promotes:
 *       0 < next < 100 → 'in_progress'
 *       next >= 100    → 'done'
 *       next === 0     → status untouched (the row stays whatever it was)
 */
export interface ProgressPatch {
  progress: number;
  progressMode: 'manual';
  status?: 'in_progress' | 'done';
}

export function buildProgressPatch(
  currentProgress: number,
  delta: number,
): ProgressPatch {
  const base = clampProgress(currentProgress);
  const next = Math.max(0, Math.min(100, base + delta));
  const patch: ProgressPatch = { progress: next, progressMode: 'manual' };
  if (next >= 100) patch.status = 'done';
  else if (next > 0) patch.status = 'in_progress';
  return patch;
}

/** Resolve the +/- delta from a progress-bar click. Shift = decrement. */
export function progressDeltaForClick(e: { shiftKey: boolean }): number {
  return e.shiftKey ? -10 : 10;
}

/**
 * Resolve the current user's `assigneeId` via the `assigneeUserMap`.
 * Returns `null` when the user isn't mapped — callers surface a CTA
 * directing the user to the Settings page.
 */
export function resolveMyAssigneeId(
  uid: UserId | null,
  mappings: readonly AssigneeUserMappingDoc[],
): string | null {
  if (!uid) return null;
  for (const m of mappings) {
    if (m.userId === uid) return m.assigneeId;
  }
  return null;
}

/** Undone === not 'done' and not 'cancelled'. */
export function isUndone(t: Pick<MyTaskItem, 'status'>): boolean {
  return t.status !== 'done' && t.status !== 'cancelled';
}

/* -------------------------------------------------------------------- */
/*  Date helpers (locale: ja, week starts Monday, ends Sunday)          */
/* -------------------------------------------------------------------- */

/** End of *today* in the runtime's local timezone (23:59:59.999). */
export function endOfDay(now: Date): Date {
  const d = new Date(now);
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * End of the current week (Sunday 23:59 local time). We pin Sunday so
 * "今週" maps to the natural Japanese Mon-Sun cadence — switching to
 * Friday would lose Saturday work the user might be planning ahead for.
 */
export function endOfWeek(now: Date): Date {
  const d = new Date(now);
  const dow = d.getDay(); // 0 = Sunday, 1 = Monday, ...
  // Days remaining until Sunday: if today is Sunday (0) it's 0; else 7-dow.
  const daysUntilSunday = dow === 0 ? 0 : 7 - dow;
  d.setDate(d.getDate() + daysUntilSunday);
  d.setHours(23, 59, 59, 999);
  return d;
}

/* -------------------------------------------------------------------- */
/*  Tab filters                                                          */
/* -------------------------------------------------------------------- */

/**
 * Items due by end-of-today (inclusive), excluding cancelled / done.
 * Items without a due date are skipped — "今日" is a hard date filter.
 */
export function filterToday(
  items: readonly MyTaskItem[],
  now: Date,
): MyTaskItem[] {
  const cutoff = endOfDay(now).getTime();
  return items.filter((t) => {
    if (!isUndone(t)) return false;
    if (!t.endDate) return false;
    return t.endDate.getTime() <= cutoff;
  });
}

/**
 * Items due by end-of-week (inclusive) plus undated migration tasks.
 * The wider net is intentional: a migration task with no plannedEnd is
 * still "active work I own" and should land somewhere visible.
 */
export function filterThisWeek(
  items: readonly MyTaskItem[],
  now: Date,
): MyTaskItem[] {
  const cutoff = endOfWeek(now).getTime();
  return items.filter((t) => {
    if (!isUndone(t)) return false;
    if (!t.endDate) return true;
    return t.endDate.getTime() <= cutoff;
  });
}

/**
 * "Blocked-by-me": undone items I own that show up as a predecessor in
 * at least one *other* item's `dependsOn` list. The successor scan is
 * project-wide, not just over my-tasks, so I see "I'm blocking 田中's
 * work" even when 田中's row isn't on my list.
 *
 * `successorCount` is denormalised for the row renderer so it can
 * surface "後続 N 件" without re-walking the dependency graph.
 */
export interface BlockedByMeRow extends MyTaskItem {
  successorCount: number;
}

export function filterBlockedByMe(
  myItems: readonly MyTaskItem[],
  allWbsItems: readonly WbsItemDoc[],
  allTasks: readonly TaskDoc[],
): BlockedByMeRow[] {
  // Build successor counts in one pass over the project graph. We index
  // by (source, id) so a WBS item and a migration task with the same
  // string id don't collide — distinct collections, distinct namespaces.
  const wbsSuccCounts = new Map<string, number>();
  for (const it of allWbsItems) {
    for (const predId of it.dependsOn) {
      wbsSuccCounts.set(predId, (wbsSuccCounts.get(predId) ?? 0) + 1);
    }
  }
  const taskSuccCounts = new Map<string, number>();
  for (const t of allTasks) {
    for (const predId of t.dependsOn) {
      taskSuccCounts.set(predId, (taskSuccCounts.get(predId) ?? 0) + 1);
    }
  }
  const out: BlockedByMeRow[] = [];
  for (const t of myItems) {
    if (!isUndone(t)) continue;
    const count =
      t.source === 'wbs'
        ? wbsSuccCounts.get(t.id) ?? 0
        : taskSuccCounts.get(t.id) ?? 0;
    if (count > 0) out.push({ ...t, successorCount: count });
  }
  return out;
}

/**
 * Done WBS rows (migration tasks have no native done state) finished
 * within the last `windowDays` (default 14). Returns most-recent first
 * so the user sees their just-shipped work at the top.
 */
export function filterDoneRecent(
  items: readonly MyTaskItem[],
  now: Date,
  windowDays = 14,
): MyTaskItem[] {
  const cutoff = now.getTime() - windowDays * 24 * 60 * 60 * 1000;
  return items
    .filter((t) => t.status === 'done' && t.recencyTime >= cutoff)
    .sort((a, b) => b.recencyTime - a.recencyTime);
}

/**
 * Single entry point used by the page + the hook tests. Keeps the
 * source→normalised translation in one place so a future schema change
 * (e.g. real status on `TaskDoc`) only needs to touch one function.
 */
export interface MyTasksBuckets {
  today: MyTaskItem[];
  thisWeek: MyTaskItem[];
  blockedByMe: BlockedByMeRow[];
  doneRecent: MyTaskItem[];
  /** All my items in unified shape; used by the "全件" fallback views. */
  all: MyTaskItem[];
}

export function buildMyTasksBuckets(input: {
  myAssigneeId: string | null;
  wbsItems: readonly WbsItemDoc[];
  tasks: readonly TaskDoc[];
  now: Date;
}): MyTasksBuckets {
  const { myAssigneeId, wbsItems, tasks, now } = input;
  if (!myAssigneeId) {
    return {
      today: [],
      thisWeek: [],
      blockedByMe: [],
      doneRecent: [],
      all: [],
    };
  }
  const my: MyTaskItem[] = [];
  for (const it of wbsItems) {
    if (it.assigneeId !== myAssigneeId) continue;
    my.push(wbsItemToMyTask(it));
  }
  for (const t of tasks) {
    if (t.assigneeId !== myAssigneeId) continue;
    my.push(taskToMyTask(t));
  }
  // Sort by endDate ascending, undated last; this is the canonical
  // "what should I do next" order. Stable for items with same date.
  my.sort((a, b) => {
    const ta = a.endDate?.getTime() ?? Number.POSITIVE_INFINITY;
    const tb = b.endDate?.getTime() ?? Number.POSITIVE_INFINITY;
    if (ta !== tb) return ta - tb;
    return a.name.localeCompare(b.name, 'ja');
  });
  return {
    today: filterToday(my, now),
    thisWeek: filterThisWeek(my, now),
    blockedByMe: filterBlockedByMe(my, wbsItems, tasks),
    doneRecent: filterDoneRecent(my, now),
    all: my,
  };
}
